// Login.js - Login page component with Tailwind + Shadcn/UI
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import { AuthBackground } from '../components/AuthBackground';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegisterLink, setShowRegisterLink] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/app';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowRegisterLink(false);
    setIsLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Login failed. Please try again.');
        if (result.action === 'register') {
          setShowRegisterLink(true);
        }
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[#06060e]">
      <AuthBackground />

      {/* Glow halo behind card */}
      {/* Outer glow — large diffuse */}
      <div
        className="absolute z-[5] w-[700px] h-[800px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(152,96,167,0.30) 0%, rgba(120,80,170,0.18) 25%, rgba(38,58,149,0.12) 50%, transparent 72%)',
          filter: 'blur(60px)',
        }}
      />
      {/* Inner glow — tight, bright */}
      <div
        className="absolute z-[5] w-[460px] h-[540px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(180,140,210,0.25) 0%, rgba(152,96,167,0.10) 50%, transparent 70%)',
          filter: 'blur(25px)',
        }}
      />

      <Card
        className="w-full max-w-[420px] relative z-10 overflow-hidden"
        style={{
          boxShadow: '0 0 0 1px rgba(200,180,230,0.2), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 0 rgba(152,96,167,0.06), 0 0 120px -25px rgba(152,96,167,0.35), 0 0 60px -10px rgba(38,58,149,0.18), 0 30px 60px -20px rgba(0,0,0,0.6)',
          backgroundColor: '#ffffff',
          borderRadius: '16px',
        }}
      >
        {/* Top-edge light catch */}
        <div
          className="absolute top-0 left-0 right-0 h-[1px] pointer-events-none z-20"
          style={{
            background: 'linear-gradient(90deg, transparent 5%, rgba(255,255,255,0.7) 30%, rgba(200,180,230,0.4) 50%, rgba(255,255,255,0.7) 70%, transparent 95%)',
          }}
        />
        {/* Left-edge highlight */}
        <div
          className="absolute top-0 left-0 bottom-0 w-[1px] pointer-events-none z-20"
          style={{
            background: 'linear-gradient(180deg, transparent 5%, rgba(255,255,255,0.3) 30%, rgba(152,96,167,0.15) 50%, rgba(255,255,255,0.3) 70%, transparent 95%)',
          }}
        />
        {/* Internal refraction overlay — faux glass shimmer */}
        <div
          className="absolute inset-0 pointer-events-none z-[1] rounded-[16px] overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(152,96,167,0.04) 0%, transparent 30%, transparent 70%, rgba(38,58,149,0.03) 100%)',
          }}
        />
        <div
          className="absolute top-0 left-0 right-0 h-24 pointer-events-none z-[1] rounded-t-[16px]"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, transparent 100%)',
          }}
        />

        <CardHeader className="text-center space-y-4 pb-2">
          <img
            src="/static/kal-cropped.svg"
            alt="Kal Logo"
            className="w-[120px] h-auto mx-auto"
          />
          <p className="text-[0.75rem] font-normal text-kaleido-gray-500 tracking-[0.01em] leading-[1.4] mt-1">
            Project Knowledge,<br />Intelligently Delivered.
          </p>
          <div>
            <CardTitle className="text-2xl font-semibold text-kaleido-gray-900">
              Welcome back
            </CardTitle>
            <CardDescription className="text-kaleido-gray-500 mt-1">
              Sign in to continue to Kal
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant={showRegisterLink ? 'default' : 'destructive'} className={showRegisterLink ? 'bg-amber-50 border-amber-300 text-amber-800' : ''}>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="ml-2">
                  <span>{error}</span>
                  {showRegisterLink && (
                    <Link
                      to="/register"
                      className="block mt-2 text-kaleido-purple font-medium hover:underline"
                    >
                      Create a new account →
                    </Link>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-kaleido-gray-700">
                Email
              </Label>
              <Input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                disabled={isLoading}
                className="h-11 border-kaleido-gray-200 bg-kaleido-gray-50 focus-visible:ring-kaleido-purple"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-kaleido-gray-700">
                Password
              </Label>
              <Input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                disabled={isLoading}
                className="h-11 border-kaleido-gray-200 bg-kaleido-gray-50 focus-visible:ring-kaleido-purple"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 mt-2 bg-gradient-to-r from-kaleido-purple to-[#8050a0] hover:from-[#8050a0] hover:to-kaleido-purple transition-all duration-300 hover:shadow-lg hover:shadow-kaleido-purple/25"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </CardContent>

        <CardFooter className="flex flex-col items-center pt-2 pb-6">
          <div className="text-center border-t border-kaleido-gray-100 pt-4 w-full">
            <p className="text-sm text-kaleido-gray-500">
              Don&apos;t have an account?{' '}
              <Link
                to="/register"
                className="text-kaleido-purple font-medium hover:underline"
              >
                Create one
              </Link>
            </p>
          </div>

          <div className="flex flex-col items-center mt-6 opacity-70">
            <span className="text-[10px] text-kaleido-gray-500 mb-1">powered by</span>
            <img
              src="/static/asset_1b.png"
              alt="Kaleido Logo"
              className="h-[18px] w-auto"
            />
          </div>
        </CardFooter>
      </Card>

      <span className="absolute bottom-3 right-4 text-xs text-kaleido-gray-500/60 font-mono z-10 select-none">
        v2.0.1
      </span>
    </div>
  );
}

export default Login;
